import React, { useState, useRef, useEffect } from 'react'
import { useWindowSize } from 'hooks'
import AddComment from './AddComment'
import styled from 'particles/themes/styled'
import IconButton from 'particles/Button/IconButton'
import Text from 'particles/Text'
import Tooltip, { TooltipPlacement } from 'particles/Tooltip'

interface ContainerProps {
    width?: string
    maxHeight?: number
}
const Container = styled.div<ContainerProps>`
    display: flex;
    flex-direction: column;
    min-width: 264px;
    width: ${({ width }) => width};
    max-width: ${({ width }) => width};
    min-height: 164px;
    padding: ${({ theme }) =>
        `${theme.spacing[300]} ${theme.spacing[100]} ${theme.spacing[300]} ${theme.spacing[300]}`};
    gap: ${({ theme }) => theme.spacing[200]};
    background-color: ${({ theme }) => theme.colors.surface.primary};
    border-radius: 4px;
    border: 1px solid ${({ theme }) => theme.colors.border.primary};
    box-sizing: border-box;
    max-height: ${({ maxHeight }) => (maxHeight ? `${maxHeight}px` : '50vh')};
    overflow: hidden;
    flex: 1 auto 1;
    .actions {
        display: flex;
    }
    .textarea-input {
        background-color: ${({ theme }) => theme.colors.surface.primary};
    }

    .card-title-block {
        display: flex;
        justify-content: space-between;
        padding-bottom: 8px;
        border-bottom: 1px solid ${({ theme }) => theme.colors.divider.primary};
        padding-right: ${({ theme }) => theme.spacing[200]};
        align-items: center;
    }
    .card-comments-container {
        width: 100%;
        display: flex;
        flex-direction: column;
        gap: ${({ theme }) => theme.spacing[400]};
        max-height: 100%;
        overflow-y: auto;
        overflow-x: hidden;
        box-sizing: border-box;
        overflow-anchor: none;
        padding-bottom: 1px;
        ::-webkit-scrollbar {
            width: 4px;
        }

        ::-webkit-scrollbar-thumb {
            border-radius: 20px;
            background-color: ${({ theme }) => theme.colors.indicator.explosiveGrey};
        }
    }
    &.active {
        border-color: ${({ theme }) => theme.colors.border.actionFocus};
        background-color: ${({ theme }) => theme.colors.surface.actionPastelHover};
        .comments {
            .textarea-input,
            textarea {
                background-color: ${({ theme }) => theme.colors.surface.actionPastelHover};
            }
        }
    }
`

const MutationConfig: MutationObserverInit = {
    childList: true,
    subtree: true,
}

interface Profile {
    uuid: string
    name: string
}

interface ButtonsAndPlaceholderCopies {
    primaryButtonLabel?: string
    secondaryButtonLabel?: string
    placeholder?: string
}
export interface CommentProps {
    profile: Profile
    timeSlots?: string
    moreActionCallback?: (e: React.MouseEvent<HTMLElement>, onEditModeCallback: () => void) => void
    value?: string
    onEditClick: (comment?: string) => void
    uuid: string
    isActive?: boolean
    isLoading?: boolean
}
export type ResolveConfig = {
    onResolve?: () => void
    isResolvable: boolean
    isResolved: boolean
    tooltip?: {
        placement?: TooltipPlacement
        label: string
    }
}
interface CommentCardProps {
    className?: string
    comments: CommentProps[]
    replyUser: Profile
    onReplyCallback?: (comment?: string) => void
    onReplyCancelCallback?: () => void
    onReplyChange?: (comment?: string) => void
    replyValue?: string
    replyCommentCopies: ButtonsAndPlaceholderCopies
    commentItemCopies: ButtonsAndPlaceholderCopies
    cardMoreActionCallback?: (e: React.MouseEvent<HTMLElement>) => void
    cardTitle: string
    cardWidth?: string
    isActive?: boolean
    maxHeight?: number
    isReplyLoading?: boolean
    resolveConfig?: ResolveConfig
}

const CommentCard: React.FC<CommentCardProps> = ({
    className,
    comments,
    replyUser,
    onReplyCallback,
    onReplyCancelCallback,
    onReplyChange,
    replyCommentCopies,
    replyValue,
    commentItemCopies,
    cardMoreActionCallback,
    cardTitle,
    cardWidth = '264px',
    isActive = false,
    maxHeight,
    isReplyLoading,
    resolveConfig,
}) => {
    const [blockWidth, setBlockWidth] = useState<number | undefined>(undefined)
    const windowSize = useWindowSize()
    const commentsRef = useRef<HTMLDivElement | null>(null)
    const observer = useRef<MutationObserver | null>(null)
    const isCommentEdit = useRef(false)

    const handleCommentsScroll = () => {
        if (commentsRef.current && !isCommentEdit.current) {
            commentsRef.current.scrollTo({ top: commentsRef.current.scrollHeight })
        }
    }

    const mutationCallback: MutationCallback = (mutationsList: { type: string }[]) => {
        for (const mutation of mutationsList) {
            if (mutation.type === 'childList' || mutation.type === 'subtree') {
                handleCommentsScroll()
            }
        }
    }

    useEffect(() => {
        if (commentsRef.current) {
            observer.current = new MutationObserver(mutationCallback)
            observer.current.observe(commentsRef.current, MutationConfig)
            setBlockWidth(commentsRef.current.clientWidth - 8)
        }

        return () => {
            if (commentsRef.current && observer.current) {
                observer.current.disconnect()
            }
        }
    }, [windowSize, cardWidth, comments])

    useEffect(() => {
        handleCommentsScroll()
    }, [comments])

    return (
        <Container width={cardWidth} className={`${className} ${isActive ? 'active' : ''}`} maxHeight={maxHeight}>
            <div className="card-title-block">
                <Text scale="caption500" color="secondary" as="div">
                    {cardTitle}
                </Text>
                <div className="actions">
                    {resolveConfig && (resolveConfig?.isResolved || resolveConfig.isResolvable) && (
                        <Tooltip
                            label={resolveConfig.tooltip?.label || ''}
                            placement={resolveConfig.tooltip?.placement || 'top'}
                        >
                            <IconButton
                                icon="checkCircle"
                                form="icon"
                                iconForm={!resolveConfig.isResolvable || resolveConfig.isResolved ? 'fill' : 'regular'}
                                isDisabled={!resolveConfig.isResolvable}
                                size="s"
                                color={!resolveConfig.isResolved ? 'neutral' : 'primary'}
                                onClick={e => {
                                    e.preventDefault()
                                    if (typeof resolveConfig?.onResolve === 'function') {
                                        resolveConfig?.onResolve()
                                    }
                                }}
                                className="more-action"
                            />
                        </Tooltip>
                    )}

                    {cardMoreActionCallback && (
                        <IconButton
                            icon="dotsThree"
                            form="icon"
                            size="s"
                            color="neutral"
                            onClick={(e: React.MouseEvent<HTMLElement>) => {
                                cardMoreActionCallback(e)
                            }}
                            className="more-action"
                        />
                    )}
                </div>
            </div>
            <div className="card-comments-container" ref={commentsRef}>
                {comments.map((comment: CommentProps) => (
                    <AddComment
                        className="comments"
                        width={blockWidth}
                        key={comment.uuid}
                        isCommentItem
                        profile={comment.profile}
                        isEditable={comment.profile.uuid === replyUser.uuid}
                        timeSlots={comment.timeSlots}
                        moreActionCallback={comment.moreActionCallback}
                        value={comment?.value}
                        onPrimaryButtonClick={comment.onEditClick}
                        hidableButtons={!comment.isLoading}
                        isLoading={comment.isLoading}
                        onFocusCallBack={() => {
                            isCommentEdit.current = true
                        }}
                        {...commentItemCopies}
                    />
                ))}
                {onReplyCallback && (
                    <AddComment
                        profile={replyUser}
                        onPrimaryButtonClick={(comment?: string) => {
                            onReplyCallback(comment)
                            handleCommentsScroll()
                        }}
                        onCommentChange={onReplyChange}
                        onSecondaryButtonClick={() => {
                            if (onReplyCancelCallback) {
                                onReplyCancelCallback()
                            }
                            handleCommentsScroll()
                        }}
                        value={replyValue}
                        hidableButtons={!isReplyLoading}
                        width={blockWidth}
                        onFocusCallBack={() => {
                            isCommentEdit.current = false
                        }}
                        autoClearComment={false}
                        isLoading={isReplyLoading}
                        {...replyCommentCopies}
                    />
                )}
            </div>
        </Container>
    )
}
export default CommentCard
